import { useQuery } from "@apollo/client";
import { useCompanyPageStore } from "../../../store/companyPageStore";
import {
  COMPANY_CONTRACTS_QUERY,
  Contract,
} from "../../../legacy/queries/Companies";
import { useMemo, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import MoneyIcon from "@mui/icons-material/AttachMoney";
import LocationIcon from "@mui/icons-material/LocationOn";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getNumberLabel } from "../../../utils/numberUtils";
import { lightScrollbarStyle } from "../../../styles/lightScrollbarStyle";
import { COMPANY_SEARCH_CLIENT } from "../../../utils/consts";

interface ContractCardProps {
  contract: Contract;
}

const ContractCard = ({ contract }: ContractCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Card sx={{ mb: 2, boxShadow: 2, "&:hover": { boxShadow: 4 } }}>
      <CardContent>
        {/* Header Section */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1}>
            <Typography
              variant="h5"
              component="h3"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {contract.title}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{ ml: 1 }}
            aria-label="expand"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        {/* Key Info Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <BusinessIcon color="action" fontSize="small" />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Primary Vendor
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {contract.primaryVendor.name}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <MoneyIcon color="action" fontSize="small" />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Deal Value
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {getNumberLabel(contract.dealValue, {
                    compact: true,
                    isCurrency: true,
                  })}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <CalendarIcon color="action" fontSize="small" />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Contract Period
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(contract.startDate)} -{" "}
                  {formatDate(contract.endDate)}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <LocationIcon color="action" fontSize="small" />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Signing Country
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {contract.signingCountry}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {/* Expandable Content */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ mb: 2 }} />

          {/* Summary */}
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Summary
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              {contract.summary}
            </Typography>
          </Box>

          {/* Contract Details Grid */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Contract Details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Customer Drivers
                    </Typography>
                    <Typography variant="body2">
                      {contract.customerDrivers}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Performance Criteria
                    </Typography>
                    <Typography variant="body2">
                      {contract.performanceCriteria}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Pricing Structure
                    </Typography>
                    <Typography variant="body2">
                      {contract.pricingStructure}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Contract Structure
                    </Typography>
                    <Typography variant="body2">
                      {contract.contractStructure}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Part of Larger Contract
                    </Typography>
                    <Typography variant="body2">
                      {contract.largerContract ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Contract Event
                    </Typography>
                    <Typography variant="body2">
                      {contract.contractEvent}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Bid Process
                    </Typography>
                    <Typography variant="body2">
                      {contract.bidProcess}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Service Lines
              </Typography>
              <Box
                sx={{
                  maxHeight: 240,
                  overflowY: "auto",
                  pr: 1,
                  ...lightScrollbarStyle,
                }}
              >
                <Stack spacing={2}>
                  {contract.serviceLines.map((serviceLine, index) => (
                    <Card key={index} variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight={500} gutterBottom>
                        {serviceLine.delivery}
                      </Typography>
                      {serviceLine.geographicScope && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Geographic Scope: {serviceLine.geographicScope}
                        </Typography>
                      )}
                      <Box mt={1}>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {serviceLine.serviceTypes.map((type, typeIndex) => (
                            <Chip
                              key={typeIndex}
                              label={type}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Card>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {/* Dates */}
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Important Dates
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Announcement Date
                </Typography>
                <Typography variant="body2">
                  {formatDate(contract.announcementDate)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body2">
                  {formatDate(contract.startDate)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  End Date
                </Typography>
                <Typography variant="body2">
                  {formatDate(contract.endDate)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Contract Term
                </Typography>
                <Typography variant="body2">{contract.term} months</Typography>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const ContractViewSkeleton = () => {
  return (
    <Box>
      <Box mb={3}>
        <Skeleton variant="text" width="40%" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={24} />
      </Box>

      {/* Contract Cards Skeletons */}
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={index} sx={{ mb: 2, boxShadow: 2 }}>
          <CardContent>
            {/* Header Section */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={2}
            >
              <Box flex={1}>
                <Skeleton
                  variant="text"
                  width="70%"
                  height={32}
                  sx={{ mb: 1 }}
                />
                <Stack direction="row" spacing={1} mb={1}>
                  <Skeleton variant="rounded" width={80} height={24} />
                  <Skeleton variant="rounded" width={90} height={24} />
                  <Skeleton variant="rounded" width={120} height={24} />
                </Stack>
              </Box>
              <Skeleton variant="circular" width={40} height={40} />
            </Stack>

            {/* Key Info Grid */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {Array.from({ length: 4 }).map((_, gridIndex) => (
                <Grid key={gridIndex} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Skeleton variant="circular" width={20} height={20} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="60%" height={16} />
                      <Skeleton variant="text" width="80%" height={20} />
                    </Box>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Pagination Skeleton */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mt={4}
        mb={2}
        gap={3}
      >
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="text" width={80} height={24} />
        <Stack direction="row" gap={0.5}>
          {Array.from({ length: 4 }).map((_, btnIndex) => (
            <Skeleton
              key={btnIndex}
              variant="circular"
              width={32}
              height={32}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export const ContractView = () => {
  const company = useCompanyPageStore((state) => state.company);
  const [page, setPage] = useState(1);
  const [contractsPerPage, setContractsPerPage] = useState(10);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, loading, error } = useQuery(COMPANY_CONTRACTS_QUERY, {
    context: { clientName: COMPANY_SEARCH_CLIENT },
    variables: {
      hgId: company?.hgId,
      per: contractsPerPage,
      page: page - 1, // Backend expects 0-based pagination
    },
    skip: !company?.hgId,
  });

  const contracts = useMemo(() => data?.contract.data ?? [], [data]);

  const totalContracts = data?.contract.count ?? 0;
  const totalPages = Math.ceil(totalContracts / contractsPerPage);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
    setTimeout(() => {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handlePerPageChange = (event: SelectChangeEvent<number>) => {
    setContractsPerPage(event.target.value as number);
    setPage(1); // Reset to first page when changing per page
    setTimeout(() => {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  if (loading) {
    return <ContractViewSkeleton />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading contracts: {error?.message ?? "Unknown error"}
      </Alert>
    );
  }

  if (contracts.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          We don't have any Contract information for this company at this time.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please contact our{" "}
          <a href="mailto:customersupport@hginsights.com">support team</a> for
          more information.
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef}>
      <Box mb={3}>
        <Typography
          data-testid="company-detail-contract-count-label"
          variant="h4"
          component="h2"
        >
          {totalContracts} Contracts found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contract agreements and partnerships for {company?.name}
        </Typography>
      </Box>

      <Box>
        {contracts.map((contract: Contract) => (
          <ContractCard key={contract.id} contract={contract} />
        ))}
      </Box>

      {/* Pagination Controls */}
      {totalContracts > 0 && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          mt={4}
          mb={2}
          gap={3}
        >
          {/* Contracts per page selector */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              Contracts per page:
            </Typography>
            <FormControl size="small">
              <Select
                data-testid="company-detail-contract-perpage-select"
                value={contractsPerPage}
                onChange={handlePerPageChange}
                variant="standard"
                sx={{
                  minWidth: 60,
                  height: 32,
                  "& .MuiSelect-select": {
                    py: 0.5,
                    px: 1,
                    fontSize: "0.875rem",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Page range info */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.875rem" }}
          >
            {(page - 1) * contractsPerPage + 1}-
            {Math.min(page * contractsPerPage, totalContracts)} of{" "}
            {totalContracts}
          </Typography>

          {/* Pagination buttons */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              onClick={() => handlePageChange(null, 1)}
              disabled={page === 1}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              <FirstPageIcon sx={{ fontSize: "0.875rem" }} />
            </IconButton>
            <IconButton
              onClick={() => handlePageChange(null, page - 1)}
              disabled={page === 1}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: "0.875rem" }} />
            </IconButton>
            <IconButton
              onClick={() => handlePageChange(null, page + 1)}
              disabled={page === totalPages}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: "0.875rem" }} />
            </IconButton>
            <IconButton
              onClick={() => handlePageChange(null, totalPages)}
              disabled={page === totalPages}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              <LastPageIcon sx={{ fontSize: "0.875rem" }} />
            </IconButton>
          </Box>
        </Stack>
      )}
    </Box>
  );
};
